import Alpine from 'alpinejs'

const CONFIG = {
  waveSpeed: 0.5,
  dotSize: 0.9,
  maxDotSize: 2.8,
  spacing: 14,
  minOpacity: 0,
  maxOpacity: 0.25,
  dotColor: { r: 180, g: 140, b: 160 },
  glowColor: { r: 220, g: 200, b: 220 },
  backgroundColor: '#004469',
  bgR: 0,
  bgG: 68,
  bgB: 105
}

// --- Precomputed exp(-x) LUT ---
const EXP_LUT_SIZE = 4096
const EXP_LUT_RANGE = 20.0
const EXP_LUT = new Float32Array(EXP_LUT_SIZE + 1)
for (let i = 0; i <= EXP_LUT_SIZE; i++) {
  EXP_LUT[i] = Math.exp(-(i / EXP_LUT_SIZE) * EXP_LUT_RANGE)
}

function fastExp (x) {
  if (x >= EXP_LUT_RANGE) return 0
  if (x <= 0) return 1
  const idx = (x * EXP_LUT_SIZE) / EXP_LUT_RANGE
  const i = idx | 0
  const frac = idx - i
  return EXP_LUT[i] + frac * (EXP_LUT[i + 1] - EXP_LUT[i])
}

const MAX_BLOBS = 16
const _bx = new Float32Array(MAX_BLOBS)
const _by = new Float32Array(MAX_BLOBS)
const _bCos = new Float32Array(MAX_BLOBS)
const _bSin = new Float32Array(MAX_BLOBS)
const _bOp = new Float32Array(MAX_BLOBS)
const _bGOp = new Float32Array(MAX_BLOBS)
const _bISX2 = new Float32Array(MAX_BLOBS) // 1/(sizeX^2)
const _bISY2 = new Float32Array(MAX_BLOBS)
const _bIGX2 = new Float32Array(MAX_BLOBS) // 1/(glowSizeX^2)
const _bIGY2 = new Float32Array(MAX_BLOBS)

document.addEventListener('alpine:init', () => {
  Alpine.data('dotWave', () => ({
    width: 0,
    height: 0,
    time: 0,
    animationFrameId: null,
    isInView: true,
    isPageVisible: true,
    prefersReducedMotion: false,
    ctx: null,
    imageData: null,
    pixels: null,
    pixelWidth: 0,
    pixelHeight: 0,

    init () {
      const canvas = this.$refs.canvas
      this.ctx = canvas.getContext('2d', { alpha: false })
      if (!this.ctx) { console.error('Could not get 2D context'); return }

      const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
      this.prefersReducedMotion = mq.matches
      mq.addEventListener('change', (e) => {
        this.prefersReducedMotion = e.matches
        this.handleVisibilityChange()
      })

      this.isPageVisible = document.visibilityState !== 'hidden'
      document.addEventListener('visibilitychange', () => {
        this.isPageVisible = document.visibilityState !== 'hidden'
        this.handleVisibilityChange()
      })

      new ResizeObserver(() => this.resize()).observe(this.$el)
      new IntersectionObserver((entries) => {
        this.isInView = entries[0].isIntersecting
        this.handleVisibilityChange()
      }).observe(this.$el)

      this.resize()
      this.handleVisibilityChange()
    },

    resize () {
      if (!this.ctx) return
      const canvas = this.$refs.canvas
      this.width = this.$el.clientWidth
      this.height = this.$el.clientHeight
      const dpr = window.devicePixelRatio || 1
      this.pixelWidth = Math.round(this.width * dpr)
      this.pixelHeight = Math.round(this.height * dpr)
      canvas.width = this.pixelWidth
      canvas.height = this.pixelHeight
      this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      this.imageData = this.ctx.createImageData(this.pixelWidth, this.pixelHeight)
      this.pixels = new Uint32Array(this.imageData.data.buffer)
      this.drawFrame()
    },

    handleVisibilityChange () {
      if (this.isInView && this.isPageVisible && !this.prefersReducedMotion) {
        this.startLoop()
      } else {
        this.stopLoop()
        if (this.isInView) {
          requestAnimationFrame(() => this.drawFrame())
        }
      }
    },

    startLoop () {
      if (!this.animationFrameId) {
        const loop = () => {
          this.drawFrame()
          this.time++
          this.animationFrameId = requestAnimationFrame(loop)
        }
        this.animationFrameId = requestAnimationFrame(loop)
      }
    },

    stopLoop () {
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId)
        this.animationFrameId = null
      }
    },

    drawFrame () {
      if (!this.ctx || !this.pixels) return

      const { dotColor, glowColor, waveSpeed, spacing, dotSize, maxDotSize, maxOpacity, bgR, bgG, bgB } = CONFIG
      const w = this.width
      const h = this.height
      const pw = this.pixelWidth
      const ph = this.pixelHeight
      const pixels = this.pixels
      const dpr = window.devicePixelRatio || 1

      // Fill background (ABGR little-endian)
      const bgPx = (0xFF << 24) | (bgB << 16) | (bgG << 8) | bgR
      pixels.fill(bgPx)

      const t = this.time * 0.01 * waveSpeed
      const { r, g, b } = dotColor
      const { r: gr, g: gg, b: gb } = glowColor

      const rows = Math.ceil(h / spacing)
      const cols = Math.ceil(w / spacing)
      const baseRadius = Math.max(w, h)
      const cyclePhase = this.time * 0.004

      const computePhase = (speed, offset, trim) => {
        const normalized = (Math.sin(cyclePhase * speed + offset) + 1) * 0.5
        if (trim >= 1) return 0
        return Math.max(0, normalized - trim) / (1 - trim)
      }

      const allBlobs = [
        {
          xF: 0.5,
          yF: 0.45,
          bsx: 0.32,
          bsy: 0.24,
          rot: 0.08,
          mOp: 0.75,
          gM: 8.0,
          ssx: 0.25,
          ssy: 0.18,
          sox: 0.0,
          soy: 1.1,
          srx: 0.4,
          sry: 0.3,
          pS: 0.6,
          pO: 0.2,
          pT: 0.15,
          mx: (t) => w * 0.5 + Math.sin(t * 0.55) * (w * 0.42) + Math.cos(t * 0.35) * (w * 0.18),
          my: (t) => h * 0.45 + Math.cos(t * 0.48) * (h * 0.36) + Math.sin(t * 0.28) * (h * 0.14)
        },
        {
          xF: 0.45,
          yF: 0.5,
          bsx: 0.26,
          bsy: 0.20,
          rot: -0.06,
          mOp: 0.6,
          gM: 8.5,
          ssx: 0.38,
          ssy: 0.25,
          sox: 0.8,
          soy: 2.1,
          srx: 0.38,
          sry: 0.28,
          pS: 0.75,
          pO: 1.1,
          pT: 0.2,
          mx: (t) => w * 0.45 + Math.cos(t * 0.6) * (w * 0.38) + Math.sin(t * 0.42) * (w * 0.16),
          my: (t) => h * 0.5 + Math.sin(t * 0.65) * (h * 0.32) + Math.cos(t * 0.32) * (h * 0.12)
        },
        {
          xF: 0.55,
          yF: 0.48,
          bsx: 0.34,
          bsy: 0.22,
          rot: 0.12,
          mOp: 0.8,
          gM: 9.0,
          ssx: 0.22,
          ssy: 0.32,
          sox: 1.6,
          soy: 0.4,
          srx: 0.45,
          sry: 0.32,
          pS: 0.55,
          pO: 2.0,
          pT: 0.12,
          mx: (t) => w * 0.55 + Math.sin(t * 0.48 + 1.2) * (w * 0.40) + Math.cos(t * 0.32) * (w * 0.15),
          my: (t) => h * 0.48 + Math.cos(t * 0.55 + 0.8) * (h * 0.34) + Math.sin(t * 0.38) * (h * 0.14)
        },
        {
          xF: 0.42,
          yF: 0.52,
          bsx: 0.20,
          bsy: 0.18,
          rot: 0.04,
          mOp: 0.5,
          gM: 7.5,
          ssx: 0.48,
          ssy: 0.42,
          sox: 2.7,
          soy: 1.3,
          srx: 0.32,
          sry: 0.25,
          pS: 0.9,
          pO: 3.4,
          pT: 0.28,
          mx: (t) => w * 0.42 + Math.sin(t * 0.62 + 2.4) * (w * 0.36) + Math.cos(t * 0.45) * (w * 0.14),
          my: (t) => h * 0.52 + Math.cos(t * 0.58 + 0.3) * (h * 0.30) + Math.sin(t * 0.35) * (h * 0.12)
        },
        {
          xF: 0.52,
          yF: 0.55,
          bsx: 0.30,
          bsy: 0.20,
          rot: 0.06,
          mOp: 0.7,
          gM: 8.5,
          ssx: 0.2,
          ssy: 0.24,
          sox: 3.9,
          soy: 2.6,
          srx: 0.42,
          sry: 0.28,
          pS: 0.5,
          pO: 4.8,
          pT: 0.14,
          mx: (t) => w * 0.52 + Math.sin(t * 0.52) * (w * 0.42) + Math.cos(t * 0.38) * (w * 0.16),
          my: (t) => h * 0.55 + Math.cos(t * 0.6) * (h * 0.32) + Math.sin(t * 0.42) * (h * 0.14)
        },
        {
          xF: 0.58,
          yF: 0.42,
          bsx: 0.18,
          bsy: 0.16,
          rot: -0.08,
          mOp: 0.45,
          gM: 7.8,
          ssx: 0.45,
          ssy: 0.28,
          sox: 4.6,
          soy: 3.2,
          srx: 0.35,
          sry: 0.22,
          pS: 1.0,
          pO: 5.6,
          pT: 0.32,
          mx: (t) => w * 0.58 + Math.cos(t * 0.58 + 1.5) * (w * 0.36) + Math.sin(t * 0.42) * (w * 0.14),
          my: (t) => h * 0.42 + Math.sin(t * 0.65 + 0.6) * (h * 0.32) + Math.cos(t * 0.32) * (h * 0.12)
        },
        {
          xF: 0.48,
          yF: 0.5,
          bsx: 0.28,
          bsy: 0.20,
          rot: 0.14,
          mOp: 0.85,
          gM: 8.8,
          ssx: 0.24,
          ssy: 0.36,
          sox: 5.8,
          soy: 3.9,
          srx: 0.4,
          sry: 0.3,
          pS: 0.7,
          pO: 6.2,
          pT: 0.15,
          mx: (t) => w * 0.48 + Math.sin(t * 0.54 + 3.1) * (w * 0.40) + Math.cos(t * 0.36) * (w * 0.16),
          my: (t) => h * 0.5 + Math.cos(t * 0.62 + 2.2) * (h * 0.34) + Math.sin(t * 0.40) * (h * 0.14)
        },
        {
          xF: 0.5,
          yF: 0.46,
          bsx: 0.22,
          bsy: 0.16,
          rot: -0.03,
          mOp: 0.55,
          gM: 7.2,
          ssx: 0.34,
          ssy: 0.4,
          sox: 6.6,
          soy: 4.5,
          srx: 0.32,
          sry: 0.26,
          pS: 0.85,
          pO: 7.3,
          pT: 0.22,
          mx: (t) => w * 0.5 + Math.cos(t * 0.5 + 4.0) * (w * 0.38) + Math.sin(t * 0.36) * (w * 0.15),
          my: (t) => h * 0.46 + Math.sin(t * 0.62 + 1.3) * (h * 0.32) + Math.cos(t * 0.40) * (h * 0.12)
        },
        {
          xF: 0.46,
          yF: 0.54,
          bsx: 0.24,
          bsy: 0.18,
          rot: 0.05,
          mOp: 0.6,
          gM: 8.2,
          ssx: 0.4,
          ssy: 0.28,
          sox: 7.5,
          soy: 5.2,
          srx: 0.38,
          sry: 0.28,
          pS: 0.6,
          pO: 8.4,
          pT: 0.18,
          mx: (t) => w * 0.46 + Math.sin(t * 0.56 + 5.0) * (w * 0.38) + Math.cos(t * 0.38) * (w * 0.16),
          my: (t) => h * 0.54 + Math.cos(t * 0.62 + 2.8) * (h * 0.34) + Math.sin(t * 0.42) * (h * 0.14)
        },
        {
          xF: 0.38,
          yF: 0.38,
          bsx: 0.26,
          bsy: 0.20,
          rot: 0.09,
          mOp: 0.65,
          gM: 8.4,
          ssx: 0.32,
          ssy: 0.38,
          sox: 8.2,
          soy: 6.1,
          srx: 0.42,
          sry: 0.32,
          pS: 0.72,
          pO: 9.5,
          pT: 0.2,
          mx: (t) => w * 0.38 + Math.sin(t * 0.68 + 1.8) * (w * 0.42) + Math.cos(t * 0.48) * (w * 0.18),
          my: (t) => h * 0.38 + Math.cos(t * 0.72 + 3.2) * (h * 0.36) + Math.sin(t * 0.38) * (h * 0.14)
        },
        {
          xF: 0.62,
          yF: 0.58,
          bsx: 0.30,
          bsy: 0.22,
          rot: -0.11,
          mOp: 0.72,
          gM: 9.0,
          ssx: 0.28,
          ssy: 0.34,
          sox: 9.0,
          soy: 7.2,
          srx: 0.45,
          sry: 0.35,
          pS: 0.58,
          pO: 10.2,
          pT: 0.16,
          mx: (t) => w * 0.62 + Math.cos(t * 0.58 + 2.6) * (w * 0.40) + Math.sin(t * 0.44) * (w * 0.16),
          my: (t) => h * 0.58 + Math.sin(t * 0.68 + 1.4) * (h * 0.34) + Math.cos(t * 0.36) * (h * 0.12)
        },
        {
          xF: 0.35,
          yF: 0.62,
          bsx: 0.18,
          bsy: 0.15,
          rot: 0.07,
          mOp: 0.5,
          gM: 7.6,
          ssx: 0.42,
          ssy: 0.32,
          sox: 10.1,
          soy: 8.0,
          srx: 0.35,
          sry: 0.28,
          pS: 0.82,
          pO: 11.0,
          pT: 0.25,
          mx: (t) => w * 0.35 + Math.sin(t * 0.64 + 4.2) * (w * 0.38) + Math.cos(t * 0.42) * (w * 0.14),
          my: (t) => h * 0.62 + Math.cos(t * 0.58 + 2.0) * (h * 0.32) + Math.sin(t * 0.34) * (h * 0.12)
        },
        {
          xF: 0.58,
          yF: 0.35,
          bsx: 0.22,
          bsy: 0.18,
          rot: -0.06,
          mOp: 0.58,
          gM: 8.0,
          ssx: 0.36,
          ssy: 0.42,
          sox: 11.2,
          soy: 9.1,
          srx: 0.38,
          sry: 0.30,
          pS: 0.68,
          pO: 12.3,
          pT: 0.22,
          mx: (t) => w * 0.58 + Math.cos(t * 0.52 + 5.5) * (w * 0.44) + Math.sin(t * 0.38) * (w * 0.18),
          my: (t) => h * 0.35 + Math.sin(t * 0.62 + 0.8) * (h * 0.36) + Math.cos(t * 0.42) * (h * 0.14)
        },
        {
          xF: 0.5,
          yF: 0.48,
          bsx: 0.28,
          bsy: 0.22,
          rot: 0.10,
          mOp: 0.7,
          gM: 8.6,
          ssx: 0.30,
          ssy: 0.36,
          sox: 12.4,
          soy: 10.2,
          srx: 0.42,
          sry: 0.34,
          pS: 0.62,
          pO: 13.5,
          pT: 0.18,
          mx: (t) => w * 0.5 + Math.sin(t * 0.72 + 3.8) * (w * 0.40) + Math.cos(t * 0.46) * (w * 0.16),
          my: (t) => h * 0.48 + Math.cos(t * 0.66 + 4.5) * (h * 0.34) + Math.sin(t * 0.40) * (h * 0.14)
        },
        {
          xF: 0.42,
          yF: 0.44,
          bsx: 0.24,
          bsy: 0.18,
          rot: -0.08,
          mOp: 0.62,
          gM: 8.2,
          ssx: 0.38,
          ssy: 0.44,
          sox: 13.6,
          soy: 11.4,
          srx: 0.40,
          sry: 0.32,
          pS: 0.76,
          pO: 14.8,
          pT: 0.24,
          mx: (t) => w * 0.42 + Math.cos(t * 0.66 + 6.2) * (w * 0.42) + Math.sin(t * 0.50) * (w * 0.18),
          my: (t) => h * 0.44 + Math.sin(t * 0.70 + 2.4) * (h * 0.36) + Math.cos(t * 0.38) * (h * 0.14)
        }
      ]

      let blobCount = 0
      for (let i = 0; i < allBlobs.length; i++) {
        const def = allBlobs[i]
        const phase = computePhase(def.pS, def.pO, def.pT)
        if (phase <= 0.04) continue

        const scale = 0.2 + phase * 0.8
        const rotation = t * def.rot
        const oscX = 0.75 + Math.sin(t * def.ssx + def.sox) * def.srx
        const oscY = 0.75 + Math.cos(t * def.ssy + def.soy) * def.sry
        const dynSX = def.bsx * baseRadius * scale * oscX
        const dynSY = def.bsy * baseRadius * scale * oscY
        const gsx = dynSX * def.gM * 0.35
        const gsy = dynSY * def.gM * 0.35

        _bx[blobCount] = def.mx(t)
        _by[blobCount] = def.my(t)
        _bCos[blobCount] = Math.cos(rotation)
        _bSin[blobCount] = Math.sin(rotation)
        _bOp[blobCount] = phase * def.mOp
        _bGOp[blobCount] = phase * def.mOp * 0.15
        _bISX2[blobCount] = 1 / (dynSX * dynSX)
        _bISY2[blobCount] = 1 / (dynSY * dynSY)
        _bIGX2[blobCount] = 1 / (gsx * gsx)
        _bIGY2[blobCount] = 1 / (gsy * gsy)
        blobCount++
      }

      const sizeDiff = maxDotSize - dotSize
      const halfSpacing = spacing * 0.5

      for (let iy = 0; iy < rows; iy++) {
        const y = iy * spacing + halfSpacing

        for (let ix = 0; ix < cols; ix++) {
          const x = ix * spacing + halfSpacing

          let totalBlob = 0
          let maxBlob = 0

          for (let bi = 0; bi < blobCount; bi++) {
            const dx = x - _bx[bi]
            const dy = y - _by[bi]
            const cos = _bCos[bi]
            const sin = _bSin[bi]
            const rx = dx * cos + dy * sin
            const ry = -dx * sin + dy * cos

            const coreArg = (rx * rx * _bISX2[bi] + ry * ry * _bISY2[bi]) * 4.0
            const coreInf = fastExp(coreArg) * _bOp[bi]

            const glowArg = (rx * rx * _bIGX2[bi] + ry * ry * _bIGY2[bi]) * 0.3
            const glowInf = fastExp(glowArg) * _bGOp[bi]

            const inf = coreInf + glowInf
            totalBlob += inf
            if (coreInf > maxBlob) maxBlob = coreInf
          }

          if (totalBlob < 0.02) continue
          if (totalBlob > 1) totalBlob = 1

          const intensity = totalBlob * totalBlob * (3 - 2 * totalBlob)
          const currentSize = dotSize + intensity * sizeDiff
          const alpha = intensity * maxOpacity

          const cb0 = maxBlob * 2.2
          const cb = cb0 > 1 ? 1 : cb0
          const scb = cb * cb * (3 - 2 * cb)
          const fR = r + (gr - r) * scb
          const fG = g + (gg - g) * scb
          const fB = b + (gb - b) * scb

          const cx = x * dpr
          const cy = y * dpr
          const rPx = currentSize * dpr
          const rPx2 = rPx * rPx
          const rCeil = (rPx + 1) | 0

          const x0 = Math.max(0, (cx - rCeil) | 0)
          const x1 = Math.min(pw - 1, (cx + rCeil) | 0)
          const y0 = Math.max(0, (cy - rCeil) | 0)
          const y1 = Math.min(ph - 1, (cy + rCeil) | 0)

          const invA = 1 - alpha
          const solidR = (fR * alpha + bgR * invA) | 0
          const solidG = (fG * alpha + bgG * invA) | 0
          const solidB = (fB * alpha + bgB * invA) | 0
          const solidPx = (0xFF << 24) | (solidB << 16) | (solidG << 8) | solidR

          for (let py = y0; py <= y1; py++) {
            const ddy = py - cy
            const ddy2 = ddy * ddy
            const row = py * pw
            for (let px = x0; px <= x1; px++) {
              const ddx = px - cx
              const d2 = ddx * ddx + ddy2
              if (d2 > rPx2) continue

              const edgeDist = rPx - Math.sqrt(d2)
              if (edgeDist >= 1.0) {
                pixels[row + px] = solidPx
              } else if (edgeDist > 0) {
                const ea = edgeDist * alpha
                const einv = 1 - ea
                pixels[row + px] = (0xFF << 24) |
                  (((fB * ea + bgB * einv) | 0) << 16) |
                  (((fG * ea + bgG * einv) | 0) << 8) |
                  ((fR * ea + bgR * einv) | 0)
              }
            }
          }
        }
      }

      this.ctx.putImageData(this.imageData, 0, 0)
    }
  }))
})
